import React from 'react';
import { styled, SxProps, Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { pink } from '@mui/material/colors';

import Symbol, { SymbolProps } from 'components/Symbol';

export enum ALERT_STATUS {
    NONE = 0,
    INFO = 1,
    WARNING = 2,
    ERROR = 3,
}

const Base = styled('div', {
    shouldForwardProp: prop =>
        prop !== '$backgroundColor' &&
        prop !== '$textColor' &&
        prop !== '$fullWidth' &&
        prop !== '$withBorder',
})<{
    $backgroundColor: string;
    $textColor: string;
    $fullWidth?: boolean;
    $withBorder?: boolean;
}>(({ theme, $backgroundColor, $textColor, $fullWidth, $withBorder }) => ({
    display: 'inline-flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2),
    userSelect: 'none',
    borderRadius: theme.shape.borderRadius,
    backgroundColor: $backgroundColor,
    color: $textColor,
    ...($fullWidth && {
        width: '100%',
    }),
    ...($withBorder && {
        border: `1px solid ${$textColor}`,
    }),
}));

const Content = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),

    textWrap: 'wrap',
    ...theme.typography.body2,
}));

const ChildrenWrapper = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    padding: theme.spacing(2),
    gap: theme.spacing(2),
    background: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
}));

interface AlertProps {
    status: ALERT_STATUS;
    text: React.ReactNode;
    style?: React.CSSProperties;
    sx?: SxProps<Theme>;
    className?: string;
    fullWidth?: boolean;
    withBorder?: boolean;
    children?: React.ReactNode;
}

export default function Alert({
    status,
    text,
    style,
    sx,
    className,
    withBorder = true,
    fullWidth,
    children,
}: AlertProps) {
    const theme = useTheme();

    const { textColor, backgroundColor, icon } = React.useMemo(() => {
        let backgroundColor: string = theme.palette.grey[200];
        let textColor: string = theme.palette.grey[900];
        let icon: Pick<SymbolProps, 'symbol' | 'filled'> = {
            symbol: 'info',
            filled: false,
        };
        switch (status) {
            case ALERT_STATUS.INFO:
                backgroundColor = theme.palette.primary[50];
                textColor = theme.palette.primary[900];
                icon = {
                    symbol: 'info',
                    filled: false,
                };
                break;
            case ALERT_STATUS.WARNING:
                backgroundColor = theme.palette.yellow[100];
                textColor = theme.palette.yellow[1000];
                icon = {
                    symbol: 'warning',
                    filled: true,
                };
                break;
            case ALERT_STATUS.ERROR:
                backgroundColor = pink[50];
                textColor = pink[900];
                icon = {
                    symbol: 'error',
                    filled: true,
                };
                break;
        }

        return {
            backgroundColor,
            textColor,
            icon,
        };
    }, [status]);

    return (
        <Base
            $backgroundColor={backgroundColor}
            $textColor={textColor}
            $fullWidth={fullWidth}
            $withBorder={withBorder}
            className={className}
            style={style}
            sx={sx}>
            <Symbol color='inherit' size='md' {...icon} />
            <Content>
                {typeof text === 'string' ? (
                    <Typography variant='body2' color='inherit'>
                        {text}
                    </Typography>
                ) : (
                    text
                )}
                {!!children && <ChildrenWrapper>{children}</ChildrenWrapper>}
            </Content>
        </Base>
    );
}
